// ── RENDERING SEZIONI PDF ────────────────────────────────────────────────────

import jsPDF from 'jspdf';
import { Palette, PdfTemplate } from './pdf-types';

export class PdfSections {
  private readonly PAGE_W = 210;
  private readonly PAGE_H = 297;
  private readonly M = 20;
  private readonly CW = 170;

  constructor(
    private pdf: jsPDF,
    private p: Palette,
    private template: PdfTemplate,
    private y_ref: { y: number },
  ) {}

  // ── UTILS ──────────────────────────────────────────────────────────────────

  nl(n = 5) {
    this.y_ref.y += n;
  }

  checkPage() {
    if (this.y_ref.y > this.PAGE_H - 18) {
      this.pdf.addPage();
      this.drawPageBg();
      this.y_ref.y = 18;
    }
  }

  drawPageBg() {
    this.pdf.setFillColor(...this.p.pageBg);
    this.pdf.rect(0, 0, this.PAGE_W, this.PAGE_H, 'F');
  }

  // ── TESTO ─────────────────────────────────────────────────────────────────

  text(
    content: string,
    size: number,
    color: [number, number, number],
    font: string,
    style: string,
    x = this.M,
    maxW = this.CW,
  ) {
    const p = this.pdf;
    p.setFont(font, style);
    p.setFontSize(size);
    p.setTextColor(...color);
    const lines = p.splitTextToSize(content, maxW);
    lines.forEach((l: string) => {
      this.checkPage();
      p.text(l, x, this.y_ref.y);
      this.nl(size * 0.5);
    });
  }

  centeredText(
    content: string,
    size: number,
    color: [number, number, number],
    font: string,
    style: string,
  ) {
    const p = this.pdf;
    p.setFont(font, style);
    p.setFontSize(size);
    p.setTextColor(...color);
    const w = p.getTextWidth(content);
    p.text(content, (this.PAGE_W - w) / 2, this.y_ref.y);
  }

  // ── SECTION TITLE ─────────────────────────────────────────────────────────

  sectionTitle(label: string) {
    this.checkPage();
    this.nl(4);
    const p = this.pdf;

    switch (this.template) {
      case 'default':
      case 'ivory':
        p.setFont('times', 'normal');
        p.setFontSize(8);
        p.setTextColor(...this.p.accent);
        p.text(label.toUpperCase(), this.M, this.y_ref.y);
        this.nl(3);
        p.setDrawColor(...this.p.accent);
        p.setLineWidth(0.4);
        p.line(this.M, this.y_ref.y, this.PAGE_W - this.M, this.y_ref.y);
        this.nl(5);
        break;

      case 'blanc':
        p.setFont('helvetica', 'bold');
        p.setFontSize(7.5);
        p.setTextColor(...this.p.light);
        p.text(label.toUpperCase(), this.M, this.y_ref.y);
        this.nl(3);
        p.setDrawColor(...this.p.border);
        p.setLineWidth(0.3);
        p.line(this.M, this.y_ref.y, this.PAGE_W - this.M, this.y_ref.y);
        this.nl(5);
        break;

      case 'blush':
        p.setFont('times', 'italic');
        p.setFontSize(9.5);
        p.setTextColor(...this.p.accent);
        p.text(label, this.M, this.y_ref.y);
        this.nl(3);
        p.setDrawColor(...this.p.border);
        p.setLineWidth(0.35);
        p.line(this.M, this.y_ref.y, this.PAGE_W - this.M, this.y_ref.y);
        this.nl(5);
        break;

      case 'noir':
        p.setFont('times', 'normal');
        p.setFontSize(8);
        p.setTextColor(...this.p.accent);
        p.text(label.toUpperCase(), this.M, this.y_ref.y);
        this.nl(3);
        p.setDrawColor(...this.p.accent);
        p.setLineWidth(0.35);
        p.line(this.M, this.y_ref.y, this.PAGE_W - this.M, this.y_ref.y);
        this.nl(5);
        break;
    }
  }

  // ── TESTO SEZIONE (singola lingua) ────────────────────────────────────────

  sectionText(content: string) {
    if (!content?.trim()) return;
    switch (this.template) {
      case 'blanc':
        this.text(content, 9.5, this.p.dark, 'helvetica', 'normal');
        break;
      default:
        this.text(content, 10, this.p.dark, 'times', 'normal');
        break;
    }
    this.nl(3);
  }

  // ── FOOTER ────────────────────────────────────────────────────────────────

  drawFooter(page: number, email: string) {
    const p = this.pdf;
    const fy = this.PAGE_H - 10;
    p.setDrawColor(...this.p.border);
    p.setLineWidth(0.3);
    p.line(this.M, fy - 3, this.PAGE_W - this.M, fy - 3);
    p.setFont('times', 'italic');
    p.setFontSize(7.5);
    p.setTextColor(...this.p.light);
    p.text(email, this.M, fy + 1);
    const pt = `${page}`;
    p.text(pt, this.PAGE_W - this.M - p.getTextWidth(pt), fy + 1);
  }

  // ── HEADERS ───────────────────────────────────────────────────────────────

  drawHeader(template: PdfTemplate, info: any) {
    switch (template) {
      case 'default':
      case 'ivory':
        this.drawHeader_ivory(info);
        break;
      case 'blanc':
        this.drawHeader_blanc(info);
        break;
      case 'blush':
        this.drawHeader_blush(info);
        break;
      case 'noir':
        this.drawHeader_noir(info);
        break;
    }
  }

  private drawHeader_ivory(info: any) {
    const p = this.pdf;
    p.setFillColor(...this.p.pageBg);
    p.rect(0, 0, this.PAGE_W, this.PAGE_H, 'F');
    this.y_ref.y = 18;
    this.centeredText(info.full_name, 17, this.p.dark, 'times', 'bold');
    this.nl(7);
    p.setDrawColor(...this.p.accent);
    p.setLineWidth(0.5);
    p.line(this.M, this.y_ref.y, this.PAGE_W - this.M, this.y_ref.y);
    this.nl(4);
    const contacts = `${info.address}  ·  ${info.telephone}  ·  ${info.email}  ·  P.IVA ${info.piva}`;
    this.centeredText(contacts, 7.5, this.p.light, 'times', 'normal');
    this.nl(4);
    p.setDrawColor(...this.p.border);
    p.setLineWidth(0.3);
    p.line(this.M, this.y_ref.y, this.PAGE_W - this.M, this.y_ref.y);
    this.nl(10);
  }

  private drawHeader_blanc(info: any) {
    const p = this.pdf;
    p.setFillColor(255, 255, 255);
    p.rect(0, 0, this.PAGE_W, this.PAGE_H, 'F');
    this.y_ref.y = 18;
    p.setFont('helvetica', 'bold');
    p.setFontSize(20);
    p.setTextColor(...this.p.dark);
    p.text(info.full_name, this.M, this.y_ref.y);
    this.nl(6);
    p.setFont('helvetica', 'normal');
    p.setFontSize(7.5);
    p.setTextColor(...this.p.light);
    p.text(`${info.address}  ·  ${info.telephone}`, this.M, this.y_ref.y);
    this.nl(4);
    p.text(`${info.email}  ·  P.IVA ${info.piva}`, this.M, this.y_ref.y);
    this.nl(5);
    p.setDrawColor(...this.p.dark);
    p.setLineWidth(1.5);
    p.line(this.M, this.y_ref.y, this.PAGE_W - this.M, this.y_ref.y);
    this.nl(10);
  }

  private drawHeader_blush(info: any) {
    const p = this.pdf;
    p.setFillColor(255, 255, 255);
    p.rect(0, 0, this.PAGE_W, this.PAGE_H, 'F');
    this.y_ref.y = 14;
    this.centeredText('— ✦ —', 10, this.p.border, 'times', 'normal');
    this.nl(7);
    this.centeredText(info.full_name, 16, this.p.dark, 'times', 'bolditalic');
    this.nl(7);
    const contacts = `${info.address}  ·  ${info.telephone}  ·  ${info.email}`;
    this.centeredText(contacts, 7.5, this.p.light, 'times', 'italic');
    this.nl(4);
    this.centeredText(
      `P.IVA ${info.piva}`,
      7.5,
      this.p.light,
      'times',
      'italic',
    );
    this.nl(5);
    p.setDrawColor(...this.p.border);
    p.setLineWidth(0.5);
    p.line(this.M + 20, this.y_ref.y, this.PAGE_W - this.M - 20, this.y_ref.y);
    this.nl(10);
  }

  private drawHeader_noir(info: any) {
    const p = this.pdf;
    p.setFillColor(...this.p.pageBg);
    p.rect(0, 0, this.PAGE_W, this.PAGE_H, 'F');
    this.y_ref.y = 16;
    p.setDrawColor(...this.p.accent);
    p.setLineWidth(0.5);
    p.line(this.M, this.y_ref.y, this.PAGE_W - this.M, this.y_ref.y);
    this.nl(8);
    this.centeredText(info.full_name, 16, this.p.accent, 'times', 'bold');
    this.nl(7);
    this.centeredText(
      `${info.address}  ·  ${info.telephone}  ·  ${info.email}  ·  P.IVA ${info.piva}`,
      7.5,
      this.p.light,
      'times',
      'italic',
    );
    this.nl(5);
    p.setDrawColor(...this.p.accent);
    p.setLineWidth(0.5);
    p.line(this.M, this.y_ref.y, this.PAGE_W - this.M, this.y_ref.y);
    this.nl(10);
  }

  // ── TITOLO CONTRATTO ──────────────────────────────────────────────────────

  contractTitle(lang: string) {
    const p = this.pdf;
    const label = lang === 'it' ? 'CONTRATTO' : 'CONTRACT';

    switch (this.template) {
      case 'default':
      case 'ivory':
        this.centeredText(label, 24, this.p.dark, 'times', 'bold');
        this.nl(5);
        p.setDrawColor(...this.p.accent);
        p.setLineWidth(0.5);
        p.line(
          (this.PAGE_W - 30) / 2,
          this.y_ref.y,
          (this.PAGE_W + 30) / 2,
          this.y_ref.y,
        );
        this.nl(10);
        break;

      case 'blanc':
        p.setFont('helvetica', 'normal');
        p.setFontSize(8);
        p.setTextColor(...this.p.light);
        p.text(
          lang === 'it' ? 'ACCORDO DI SERVIZIO' : 'SERVICE AGREEMENT',
          this.M,
          this.y_ref.y,
        );
        this.nl(5);
        p.setFont('helvetica', 'bold');
        p.setFontSize(30);
        p.setTextColor(...this.p.dark);
        p.text(lang === 'it' ? 'Contratto' : 'Contract', this.M, this.y_ref.y);
        this.nl(12);
        break;

      case 'blush':
        this.centeredText(
          lang === 'it' ? '— Contratto —' : '— Contract —',
          22,
          this.p.dark,
          'times',
          'bolditalic',
        );
        this.nl(5);
        this.centeredText(
          lang === 'it' ? 'Accordo di Servizio' : 'Service Agreement',
          9,
          this.p.accent,
          'times',
          'italic',
        );
        this.nl(10);
        break;

      case 'noir':
        this.centeredText(
          lang === 'it' ? 'C O N T R A T T O' : 'C O N T R A C T',
          20,
          this.p.accent,
          'times',
          'bold',
        );
        this.nl(5);
        this.centeredText(
          lang === 'it' ? 'Accordo di Servizio' : 'Service Agreement',
          9,
          this.p.mid,
          'times',
          'italic',
        );
        this.nl(10);
        break;
    }
  }

  // ── FIRME ─────────────────────────────────────────────────────────────────

  drawSignatures(customer_data: any, info: any, lang: string) {
    const p = this.pdf;
    this.sectionTitle(lang === 'it' ? 'Firme' : 'Signatures');
    this.nl(8);
    p.setFont('times', 'normal');
    p.setFontSize(9);
    p.setTextColor(...this.p.mid);

    const clientLabel = lang === 'it' ? 'Cliente' : 'Client';
    const providerLabel = lang === 'it' ? 'Fornitore' : 'Service Provider';

    // Label affiancate
    p.text(clientLabel, this.M, this.y_ref.y);
    p.text(providerLabel, this.PAGE_W / 2 + 5, this.y_ref.y);
    this.nl(4);

    // Firma cliente e firma fornitore affiancate
    if (info.bride_signature && info.bride_signature.length > 100) {
      p.addImage(info.bride_signature, 'PNG', this.M, this.y_ref.y - 2, 55, 22);
    }
    if (info.signature && info.signature.length > 100) {
      p.addImage(
        info.signature,
        'PNG',
        this.PAGE_W / 2 + 5,
        this.y_ref.y - 2,
        55,
        22,
      );
    }
    this.nl(20);

    // Linee firma
    p.setDrawColor(...this.p.border);
    p.setLineWidth(0.35);
    p.line(this.M, this.y_ref.y, this.M + 68, this.y_ref.y);
    p.line(
      this.PAGE_W / 2 + 5,
      this.y_ref.y,
      this.PAGE_W / 2 + 73,
      this.y_ref.y,
    );
    this.nl(4);

    // Nomi
    p.setFont('times', 'italic');
    p.setFontSize(8);
    p.setTextColor(...this.p.light);
    p.text(
      `${customer_data.client.name} ${customer_data.client.lastname}`,
      this.M,
      this.y_ref.y,
    );
    p.text(info.full_name || '', this.PAGE_W / 2 + 5, this.y_ref.y);

    // Data firma sotto i nomi (cliente e fornitore)
    const locale = lang === 'it' ? 'it-IT' : 'en-GB';
    const rawDate = customer_data.event?.contract?.dateOfContract;
    const signDate = (rawDate ? new Date(rawDate) : new Date()).toLocaleDateString(
      locale,
    );
    const signedLabel =
      lang === 'it' ? `Firmato il ${signDate}` : `Signed on ${signDate}`;

    const hasClientSign =
      info.bride_signature && info.bride_signature.length > 100;
    const hasProviderSign = info.signature && info.signature.length > 100;

    if (hasClientSign || hasProviderSign) {
      this.nl(5);
      p.setFontSize(7.5);
      if (hasClientSign) p.text(signedLabel, this.M, this.y_ref.y);
      if (hasProviderSign)
        p.text(signedLabel, this.PAGE_W / 2 + 5, this.y_ref.y);
    }
  }
}
